import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

function Badge({ children }) {
  return (
    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700">
      {children}
    </span>
  );
}

export default function ITAuditPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getItAuditLogs();
      setRows(res?.data?.data || []);
    } catch (err) {
      console.error("ITAuditPage load error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load IT audit.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      [
        row.entity_type,
        row.action_type,
        row.changed_by_name,
        row.changed_by_email,
        row.reason,
        row.source_api,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, q]);

  if (loading) return <div className="p-6 text-black dark:text-white">Loading IT audit...</div>;
  if (error) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-black dark:text-white">IT Audit</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Audit trail for IT admin actions, mappings and scope changes.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search IT audit..."
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">When</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Action</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Entity</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Changed By</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Reason</th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">Detail</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition">
                    <td className="p-3 text-black dark:text-white">{row.changed_at}</td>
                    <td className="p-3"><Badge>{row.action_type}</Badge></td>
                    <td className="p-3 text-black dark:text-white">
                      {row.entity_type} #{row.entity_id || "-"}
                    </td>
                    <td className="p-3 text-black dark:text-white">
                      <div>{row.changed_by_name || "-"}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.changed_by_email || ""}</div>
                    </td>
                    <td className="p-3 text-gray-700 dark:text-gray-300">{row.reason || "-"}</td>
                    <td className="p-3">
                      <button
                        onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-black dark:text-white"
                      >
                        {expandedId === row.id ? "Hide" : "Show"}
                      </button>
                    </td>
                  </tr>

                  {expandedId === row.id && (
                    <tr className="border-b border-gray-100 dark:border-neutral-800">
                      <td colSpan={6} className="p-4 bg-gray-50 dark:bg-neutral-950">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm font-semibold text-black dark:text-white mb-2">Old Values</div>
                            <pre className="text-xs whitespace-pre-wrap text-black dark:text-white">
                              {JSON.stringify(
                                typeof row.old_values === "string" ? JSON.parse(row.old_values || "null") : row.old_values,
                                null,
                                2
                              )}
                            </pre>
                          </div>

                          <div>
                            <div className="text-sm font-semibold text-black dark:text-white mb-2">New Values</div>
                            <pre className="text-xs whitespace-pre-wrap text-black dark:text-white">
                              {JSON.stringify(
                                typeof row.new_values === "string" ? JSON.parse(row.new_values || "null") : row.new_values,
                                null,
                                2
                              )}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No IT audit logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}